import React, { useEffect, useState } from "react";
import ActionsButton from "../../components/ActionsButton";
import ConfirmPopup from "../../components/ConfirmPopup";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import StaffCreate from "./StaffCreate";
import { formatDate, formatName, formatDateTime, trimStringFields} from "../../utils/format";
import {
  fetchStaff,
  togglePanel,
  deleteStaff,
  setOnConfirmDelete,
  setDeleteId,
  setSearchQuery,
  setSelectedStaff,
  setCurrentPage,
  TableHeader,
  handleSortByHeader,
  setSuccess,
  setError,
  setPassOpen,
  setIsOpenPanel,
  clearSelectedStaff,
  setActionName, setActionStatus,
} from "../../reducers/companyStaff";
import CompanyHeader from "../../components/CompanyHeader";
import CompanySideMenu from "../../components/CompanySideMenu";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import CustomPagination from "../../components/CustomPagination";
import noAvatarAdmin from "../../../assets/img/noAvatarAdmin.svg";
import noAvatarAdminText from "../../../assets/img/noAvatarAdminText.svg";
import { PERMISSIONS } from '../../constants/index';
import TextInput from "../../components/TextInput";
import CustomButton from "../../components/CustomButton";
import Copyright from "../../components/Copyright";

const StaffList = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const currentUser = useSelector((state: any) => state.currentUser);
  const { staffPermission } = currentUser;
  const navigate = useNavigate();
  const {
    staffList,
    loading,
    success,
    error,
    isPanelOpen,
    sortBy,
    sortOrder,
    isOnConfirmDelete,
    deleteId,
    searchQuery,
    confirmText,
    countPage,
    currentPage,
    countItems,
    pageSize,
    isReloadStaff,
    tableHeaders,
    selectedStaff,
    actionName, actionStatus,
  } = useSelector((state: any) => state.companyStaff);
  //Access to this page is denied)
  if (!staffPermission[PERMISSIONS.USER_MENU]) {
    return null;
  }

  const fetchStaffList = (page = currentPage, search=searchQuery) => {
    let data = {
      sortBy: sortBy,
      sortOrder: sortOrder,
      page: page,
      pageSize: pageSize,
      search: search,
    };
    data = trimStringFields(data);
    dispatch(fetchStaff(data));
    dispatch(setCurrentPage(page));
  }

  useEffect(() => {
    fetchStaffList();
  }, [sortBy, sortOrder, currentPage, isReloadStaff]);

  const handleChangePage = (pageNumber: any, pageSizeInput: any) => {
    dispatch(setCurrentPage(pageNumber));
  };

  const handleClose = () => {
    dispatch(togglePanel());
  };
  const selectedUser = () => {};

  const onOpenPanel = () => {
    dispatch(setSelectedStaff(null))
    dispatch(togglePanel());
  };

  const handleSort = (sortHeader: TableHeader) => {
    dispatch(handleSortByHeader(sortHeader));
  };

  const confirmDeleteStaff = (deleteId: any) => {
    const data = {
      id: deleteId,
    };
    dispatch(deleteStaff(data));
    dispatch(setOnConfirmDelete(false));
  };

  const cancelDeleteStaff = () => {
    dispatch(setDeleteId(0));
    dispatch(setOnConfirmDelete(false));
  };

  const handleSetSearchQuery = (value: string) => {
    dispatch(setSearchQuery(value));
  };

  const handeSearchStaff = (event: any) => {
    if (event.key === "Enter") {
      fetchStaffList(1);
    }
  };

  const handleClearSearch = () => {
    handleSetSearchQuery("");
    fetchStaffList(1, "");
  }

  const handleEditClick = (staff: any) => {
    dispatch(setSelectedStaff(staff));
    dispatch(setOnConfirmDelete(false));
  };

  useEffect(() => {
    if (actionName == "fetchStaff" && actionStatus == "rejected") {
      dispatch(setActionName(""));
      dispatch(setActionStatus("idle"));
      return;
    }

    if(success){
      enqueueSnackbar("I love snacks.", {
        content: (key) => (
          <CustomToast
            title={"メッセージ"}
            message={success}
            variant={"success"}
            snackbarKey={key}
            closeSnackbar={closeSnackbar}
          />
        ),
      });
      dispatch(setSuccess(null));
    }
    else if(error) {
      enqueueSnackbar("I love snacks.", {
        content: (key) => (
          <CustomToast
            title={"メッセージ"}
            message={error}
            variant={"error"}
            snackbarKey={key}
            closeSnackbar={closeSnackbar}
          />
        ),
      });
      dispatch(setError(null));
    }
  }, [error, success]);

  const handleCloseOnEscKeyDown = (event:any) => {
    const activeElement = document.activeElement as HTMLElement | null;

    // Check if the active element is an input or textarea
    if (event.key === "Escape") {
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA")
      ) {
        activeElement.blur(); // Defocus the input field
        return; // Stop further execution
      } else {
        // If no input field is focused, close the panel
        dispatch(clearSelectedStaff());
      }
    }
  };

  useEffect(() => {
    if (!isPanelOpen) {
      dispatch(clearSelectedStaff());
    }

    if (isPanelOpen) {
      window.addEventListener("keydown", handleCloseOnEscKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleCloseOnEscKeyDown);
    };
  }, [isPanelOpen]);

  return (
    <>
      <CompanyHeader />
      <div className="mt-6 px-6 flex gap-6 relative">
        <CompanySideMenu />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold leading-[1.875rem] alu-text-base-2">
              管理ユーザー
            </h1>
            <div className="relative">
              <span className="absolute top-[0.25rem] left-4 bottom-[0.25rem] flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_8574_3270)">
                    <path
                      d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                      fill="#808080"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_8574_3270">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              <TextInput
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e:any) => handleSetSearchQuery(e.target.value)}
                onKeyDown={(e:any) => handeSearchStaff(e)}
                placeholder="検索"
                searchIcon={true}
                onClearQuery={() => handleClearSearch()}
              />
            </div>
          </div>
          <div className="mt-6">
            <div className="rounded-t-lg px-4 py-2 bg-white border-b alu-border">
              <CustomButton
                onClick={onOpenPanel}
                variant="text"
              >
                追加
              </CustomButton>
            </div>
            <div className="bg-white rounded-b-lg">
              <table className="table-fixed w-full">
                <thead className="border-b alu-border px-4 py-2">
                  <tr>
                    {tableHeaders.map((header:TableHeader, idx:number) => (
                      <th key={idx} className={`px-4 py-2 text-left ${
                        idx === tableHeaders.length - 1 ? "w-[3rem] px-0" : ""
                      } ${header.title === "権限" ? "w-32" : ""} ${
                        header.title === "氏名" ? "min-w-[7rem]" : ""
                      }`}>
                        <div onClick={() => header.isSort && handleSort(header)} className={`flex items-center ${header.isSort ? 'hover:cursor-pointer' : ''}`}>
                          <p className={`${sortBy === header.field ? 'font-bold underline underline-offset-4 alu-text-base-2' : ' font-normal alu-text-base-1'} text-sm leading-[1.3125]`}>
                            {header.title}
                          </p>
                          {header.isSort && (
                            <button className={`${sortOrder === "asc" && sortBy === header.field ? 'rotate-180' : 'rotate-0'}`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <g clipPath="url(#clip0_8574_176)">
                                  <path
                                    d="M7 10L12 15L17 10H7Z"
                                    fill="#808080"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_8574_176">
                                    <rect width="24" height="24" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </button>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {staffList.map((staff: any, idx: any) => (
                    <tr key={idx} className={`hover:bg-[#fff3e5] border-t border-t-[#E0E0E0] ${selectedStaff?.id === staff?.id ? 'outline outline-1 outline-[#FF861A]' : ''}`}>
                      <td className="min-w-[7rem] px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 relative shrink-0">
                            <img
                                src={staff.avatar ? staff.avatar : noAvatarAdmin}
                                alt={`ava-${idx}`}
                                className="w-full h-full rounded-full object-cover"
                                />
                            {!staff.avatar && <img src={noAvatarAdminText} alt="" className="absolute left-0 bottom-0 w-5 h-[0.625rem]" />}
                          </div>
                          <p className="hover:cursor-pointer text-link-with-hover" onClick={() => {handleEditClick(staff)}}>
                            {formatName(staff.first_name, staff.last_name)}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        {staff.department.name}
                      </td>
                      <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        {staff.position.name}
                      </td>
                      <td className="break-words whitespace-normal px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        {staff.email}
                      </td>
                      <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        {staff.staff_type_display.name}
                      </td>
                      <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        {formatDate(staff.created_at)}
                      </td>
                      <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        {formatDateTime(staff.updated_at)}
                      </td>
                      <td className="w-[3rem] px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                        <ActionsButton
                          actionList={staff.actions}
                          staff={staff}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isOnConfirmDelete && confirmText && (
              <ConfirmPopup
                type="delete"
                open={isOnConfirmDelete}
                onClose={cancelDeleteStaff}
                confirmContent={confirmText}
                onConfirm={() => confirmDeleteStaff(deleteId)}
              />
            )}
          </div>
          {countPage > 1 && (
          <div className="flex items-center justify-center mb-10 mt-4">
              <CustomPagination 
                  total={countItems} 
                  currentPage={currentPage}
                  pageSize={pageSize} 
                  onPageChange={handleChangePage} 
              />
          </div>
          )}
        </div>
        {/* top-[-1rem] ~ (8px - 24px) minus the margin top prop of parent element */}
        {isPanelOpen && (
          <div className="fixed right-2 top-2 bottom-2 z-[999]">
            <StaffCreate onClose={handleClose} selectedUser={selectedUser} />
          </div>
        )}
      </div>
      <Copyright view="company" />
    </>
  );
};

export default StaffList;
